import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import VisitForm, { EMPTY_VISIT } from "@/components/VisitForm";
import { CheckCircle2, Loader2, Globe, Mail, Phone, Maximize2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESET_AFTER = 12;
const IDLE_MS = 120000;

const Contacts = ({ kiosk }) => {
  const Tag = kiosk ? "span" : "a";
  return (
    <div className="space-y-1.5 pt-3 text-sm text-[#1B4383]">
      <Tag href="https://www.somcoregroup.com" className="flex items-center justify-center gap-2 font-semibold"><Globe className="h-4 w-4" />www.somcoregroup.com</Tag>
      <Tag href="mailto:info@somcoregroup.com" className="flex items-center justify-center gap-2"><Mail className="h-4 w-4" />info@somcoregroup.com</Tag>
      <Tag href="tel:+255669097454" className="flex items-center justify-center gap-2"><Phone className="h-4 w-4" />+255 669 097 454</Tag>
    </div>
  );
};

export default function EventVisit() {
  const { code } = useParams();
  const [q] = useSearchParams();
  const kiosk = q.get("kiosk") === "1";
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState("");
  const [v, setV] = useState(EMPTY_VISIT);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [left, setLeft] = useState(RESET_AFTER);
  const idle = useRef(null);
  const token = kiosk ? localStorage.getItem("somcore_token") : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const reset = useCallback(() => {
    setV(EMPTY_VISIT); setDone(false); setErr(""); setFormKey((k) => k + 1); setLeft(RESET_AFTER); window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!kiosk || !done) return undefined;
    const t = setInterval(() => setLeft((s) => { if (s <= 1) { reset(); return RESET_AFTER; } return s - 1; }), 1000);
    return () => clearInterval(t);
  }, [kiosk, done, reset]);
  useEffect(() => {
    if (!kiosk || done) return undefined;
    clearTimeout(idle.current);
    if (v !== EMPTY_VISIT) idle.current = setTimeout(reset, IDLE_MS);
    return () => clearTimeout(idle.current);
  }, [kiosk, done, v, reset]);

  const scan = async (dataUrl, type) => {
    setScanning(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/public/events/${code}/scan-card`, { file_base64: dataUrl, content_type: type }, { headers });
      setV((p) => ({ ...p, name: data.contact_name || p.name, company: data.company || data.name || p.company, job_title: data.job_title || p.job_title,
        email: data.email || p.email, phone: data.whatsapp || data.phone || (data.phones || [])[0] || p.phone, website: data.website || p.website, address: data.address || p.address }));
    } catch (e) { setErr(e.response?.data?.detail || "Couldn't Read The Card — Please Type Your Details."); } finally { setScanning(false); }
  };
  useEffect(() => {
    axios.get(`${API}/public/events/${code}`).then((r) => setEv(r.data)).catch(() => setErr("This Event Link Is Not Active."));
  }, [code]);
  const submit = async () => {
    setBusy(true);
    try { await axios.post(`${API}/public/events/${code}/visits`, { ...v, kiosk }, { headers }); setDone(true); }
    catch (e) { setErr(e.response?.data?.detail || "Something Went Wrong — Please Try Again."); }
    finally { setBusy(false); }
  };
  const full = () => { (document.documentElement.requestFullscreen?.() || Promise.resolve()).catch(() => {}); };

  return (
    <div className="min-h-screen bg-[#0F2347]" data-testid={kiosk ? "event-kiosk-page" : "event-visit-page"}>
      {kiosk && (
        <div className="flex items-center justify-between px-4 pt-3 text-[11px] text-white/50">
          <span data-testid="kiosk-status">{token ? "Stand Tablet Mode" : "Stand Tablet Mode — Sign In To Somcore Hub On This Tablet For Unlimited Entries"}</span>
          <button onClick={full} className="inline-flex items-center gap-1 rounded-md border border-white/20 px-2 py-1 text-white/70 hover:text-white" data-testid="kiosk-fullscreen"><Maximize2 className="h-3.5 w-3.5" />Full Screen</button>
        </div>
      )}
      <div className={`mx-auto px-4 pb-10 ${kiosk ? "max-w-2xl pt-6" : "max-w-lg pt-8"}`}>
        <div className="mb-6 text-center text-white">
          <img src="/somcore-logo-white.png" alt="Somcore" className={`mx-auto ${kiosk ? "h-10" : "h-8"}`} />
          <p className="mt-2 text-sm text-white/80">At The Core Of Global Resources.</p>
          {ev && <p className="mt-4 text-sm font-semibold" data-testid="event-visit-title">{ev.name}{ev.stand ? ` · Stand ${ev.stand}` : ""}</p>}
        </div>
        <div className={`rounded-2xl bg-white shadow-xl ${kiosk ? "p-7" : "p-5"}`}>
          {!ev && !err ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#1B4383]" /></div>
            : done ? (
              <div className="space-y-3 py-6 text-center" data-testid="event-visit-done">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
                <h1 className="font-heading text-xl font-bold text-[#12294B]">Thank You, {v.name.split(" ")[0]}!</h1>
                <p className="text-sm text-slate-600">Great To Meet You. Our Team Will Be In Touch Shortly{v.email ? " — We've Also Sent You A Thank-You Email." : "."}</p>
                <Contacts kiosk={kiosk} />
                {kiosk && (
                  <div className="pt-4">
                    <button onClick={reset} className="w-full rounded-xl bg-[#1B4383] py-4 text-base font-bold text-white hover:bg-[#16386e]" data-testid="kiosk-next-visitor">Next Visitor</button>
                    <p className="mt-2 text-xs text-slate-400" data-testid="kiosk-countdown">Resetting For The Next Visitor In {left}s</p>
                  </div>
                )}
              </div>
            ) : err && !ev ? <p className="py-10 text-center text-sm text-slate-600">{err}</p> : (
              <>
                <h1 className={`font-heading font-bold text-[#12294B] ${kiosk ? "text-2xl" : "text-lg"}`}>{kiosk ? "Welcome To Somcore" : "Thanks For Visiting Somcore"}</h1>
                <p className="mb-4 text-sm text-slate-600">Leave Your Details And We'll Follow Up With What You Need.</p>
                {err && <p className="mb-3 rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="event-visit-error">{err}</p>}
                <VisitForm key={formKey} brands={ev.brands} types={ev.visitor_types} value={v} onChange={setV} onSubmit={submit} busy={busy} onScan={scan} scanning={scanning} askAi submitLabel="Send My Details" />
              </>
            )}
        </div>
      </div>
    </div>
  );
}
